import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { TerminalSquare, Github, Linkedin } from 'lucide-react';

// A playable, fake dev terminal. It's the portfolio's signature interactive
// feature: it shows off full-stack range (frontend UI dressed as a shell,
// "API" style responses that sound backend-y) while giving visitors
// something genuinely fun to poke at.

const PROJECTS = [
  { name: 'ai-chat-app', desc: 'AI-powered multilingual chatbot', url: 'https://ai-asistance-chat-app-nl2t.vercel.app/' },
  { name: 'thadda', desc: 'Quiz game platform (Next.js)', url: 'https://www.thadda.com/' },
  { name: 'gourmet-delight', desc: 'Food ordering & table reservations', url: 'https://gourment-delight.vercel.app/' },
  { name: 'si-cargo', desc: 'Cargo booking website', url: 'https://cargo-service.vercel.app/' },
  { name: 'tesla-clone', desc: 'Front-end Tesla clone', url: 'https://tesla-clone-chi-three.vercel.app/' },
];

const SKILLS = {
  frontend: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Framer Motion'],
  backend: ['Node.js', 'Express', 'REST APIs', 'Python'],
  database: ['MongoDB', 'PostgreSQL', 'SQL'],
  tools: ['Docker', 'AWS', 'Git', 'GitHub'],
};

const COMMAND_LIST = [
  'help', 'whoami', 'about', 'skills', 'projects', 'contact',
  'resume', 'social', 'sudo hire-me', 'coffee', 'date', 'clear',
];

const Line = ({ children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
    className={`leading-relaxed ${className}`}
  >
    {children}
  </motion.div>
);

const Prompt = ({ cmd }) => (
  <div className="flex flex-wrap gap-x-2 text-sm sm:text-base">
    <span className="text-mint-400">guest@tauqeer</span>
    <span className="text-white/30">:</span>
    <span className="text-indigo-300">~</span>
    <span className="text-white/30">$</span>
    <span className="text-white">{cmd}</span>
  </div>
);

const welcomeOutput = [
  "Welcome to Tauqeer's terminal — type 'help' to see what's inside.",
  "Tip: try 'sudo hire-me' if you like what you see. 😉",
];

function buildResponse(raw) {
  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();

  if (lower === '') return null;

  if (lower === 'help') {
    return {
      lines: [
        'Available commands:',
        ...COMMAND_LIST.map((c) => `  • ${c}`),
        "Use ↑ / ↓ to reuse previous commands, Tab to autocomplete.",
      ],
    };
  }

  if (lower === 'whoami') {
    return {
      lines: [
        'Muhammad Tauqeer',
        'Full-Stack Developer (MERN) · Bamberg, Germany',
        "Turning coffee and curiosity into shipped products.",
      ],
    };
  }

  if (lower === 'about') {
    return {
      lines: [
        "I build end-to-end: pixel-perfect React front ends wired to",
        "Node/Express APIs, backed by MongoDB or PostgreSQL, shipped on",
        "Docker + AWS. I like UIs that feel alive as much as code that",
        "scales quietly in the background.",
      ],
    };
  }

  if (lower === 'skills' || lower === 'stack') {
    return {
      lines: [
        `frontend  → ${SKILLS.frontend.join(', ')}`,
        `backend   → ${SKILLS.backend.join(', ')}`,
        `database  → ${SKILLS.database.join(', ')}`,
        `tools     → ${SKILLS.tools.join(', ')}`,
      ],
    };
  }

  if (lower === 'projects' || lower === 'ls' || lower === 'ls projects') {
    return {
      lines: [
        'Fetching /api/projects ...',
        '200 OK',
        '[',
        ...PROJECTS.map(
          (p, i) =>
            `  { "name": "${p.name}", "desc": "${p.desc}", "url": "${p.url}" }${
              i < PROJECTS.length - 1 ? ',' : ''
            }`
        ),
        ']',
        "Scroll down to 'Portfolio' to see these with screenshots.",
      ],
    };
  }

  if (lower === 'contact') {
    return {
      lines: [
        'email    → Tauqeer248@gmail.com',
        'phone    → +49 15510793298',
        'location → Bamberg, Germany',
        "Or just scroll to the 'Contact' section below.",
      ],
    };
  }

  if (lower === 'resume' || lower === 'cv') {
    window.open('/Muhammad-Tauqeer-Cv.pdf', '_blank', 'noopener,noreferrer');
    return { lines: ['Opening résumé in a new tab...'] };
  }

  if (lower === 'social' || lower === 'github' || lower === 'linkedin') {
    return {
      lines: [
        'github   → github.com/mtauqeer248',
        'linkedin → linkedin.com/in/muhammad-tauqeer-react-developer',
      ],
    };
  }

  if (lower === 'sudo hire-me') {
    return {
      lines: [
        '[sudo] password for guest: ********',
        'Permission granted. ✅',
        'Initiating hire sequence...',
        "Best way in: Tauqeer248@gmail.com — I'll reply fast.",
      ],
      highlight: true,
    };
  }

  if (lower === 'coffee') {
    return {
      lines: [
        '        ( (',
        '         ) )',
        '      ........',
        '      |      |]',
        '      \\      /',
        "       `----'",
        'Brewing... productivity +100%.',
      ],
    };
  }

  if (lower === 'date') {
    return { lines: [new Date().toString()] };
  }

  if (lower === 'clear') {
    return { clear: true };
  }

  return {
    lines: [`command not found: ${trimmed}`, "type 'help' to see what's available"],
    error: true,
  };
}

const Terminal = () => {
  const [history, setHistory] = useState([]); // { cmd, output }
  const [input, setInput] = useState('');
  const [cmdLog, setCmdLog] = useState([]);
  const [logIndex, setLogIndex] = useState(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  const runCommand = (raw) => {
    const response = buildResponse(raw);
    if (!response) return;

    if (response.clear) {
      setHistory([]);
    } else {
      setHistory((prev) => [...prev, { cmd: raw, ...response }]);
    }

    if (raw.trim()) {
      setCmdLog((prev) => [...prev, raw]);
    }
    setLogIndex(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runCommand(input);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!cmdLog.length) return;
      const nextIndex = logIndex === null ? cmdLog.length - 1 : Math.max(0, logIndex - 1);
      setLogIndex(nextIndex);
      setInput(cmdLog[nextIndex]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (logIndex === null) return;
      const nextIndex = logIndex + 1;
      if (nextIndex >= cmdLog.length) {
        setLogIndex(null);
        setInput('');
      } else {
        setLogIndex(nextIndex);
        setInput(cmdLog[nextIndex]);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const match = COMMAND_LIST.find((c) => c.startsWith(input.toLowerCase()));
      if (match) setInput(match);
    }
  };

  const quickCommands = ['help', 'whoami', 'skills', 'projects', 'sudo hire-me', 'contact'];

  return (
    <section
      id="terminal"
      className="relative py-20 sm:py-28 bg-ink-950 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grain pointer-events-none" />
      <div className="absolute -top-24 right-0 w-72 h-72 bg-indigo-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 left-0 w-72 h-72 bg-mint-500/10 rounded-full blur-3xl" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <span className="section-eyebrow">Try it yourself</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold font-display">
            <span className="text-gradient">Poke around my terminal</span>
          </h2>
          <p className="mt-4 text-white/50 max-w-xl mx-auto font-body">
            A tiny shell running entirely in your browser. Type a command, or tap one below —
            it's the fastest way to see how I think about front end and back end at once.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          onClick={focusInput}
          className="glass rounded-2xl overflow-hidden shadow-2xl shadow-black/50 cursor-text"
        >
          {/* window chrome */}
          <div className="flex items-center gap-2 px-4 py-3 bg-white/[0.03] border-b border-white/[0.06]">
            <span className="w-3 h-3 rounded-full bg-red-400/70" />
            <span className="w-3 h-3 rounded-full bg-yellow-400/70" />
            <span className="w-3 h-3 rounded-full bg-green-400/70" />
            <span className="ml-3 flex items-center gap-2 text-xs text-white/40 font-mono">
              <TerminalSquare className="w-3.5 h-3.5" />
              tauqeer@fullstack: ~
            </span>
          </div>

          {/* output */}
          <div
            ref={scrollRef}
            className="h-72 sm:h-80 overflow-y-auto px-4 sm:px-5 py-4 font-mono text-xs sm:text-sm space-y-3"
          >
            {history.length === 0 && (
              <div className="text-white/40 space-y-1">
                {welcomeOutput.map((l, i) => (
                  <Line key={i}>{l}</Line>
                ))}
              </div>
            )}

            {history.map((entry, i) => (
              <div key={i} className="space-y-1">
                <Prompt cmd={entry.cmd} />
                <div
                  className={`pl-1 ${
                    entry.error
                      ? 'text-red-300/80'
                      : entry.highlight
                      ? 'text-mint-300'
                      : 'text-white/70'
                  }`}
                >
                  {entry.lines?.map((l, j) => <Line key={j}>{l}</Line>)}
                </div>
              </div>
            ))}

            {/* live input line */}
            <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-x-2">
              <span className="text-mint-400">guest@tauqeer</span>
              <span className="text-white/30">:</span>
              <span className="text-indigo-300">~</span>
              <span className="text-white/30">$</span>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-label="Terminal command input"
                className="flex-1 min-w-[80px] bg-transparent outline-none text-white caret-mint-400"
              />
              <span className="w-2 h-4 bg-mint-400 animate-blink" />
            </form>
          </div>

          {/* quick command chips */}
          <div className="flex flex-wrap gap-2 px-4 sm:px-5 py-4 border-t border-white/[0.06] bg-white/[0.02]">
            {quickCommands.map((c) => (
              <button
                key={c}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  runCommand(c);
                  focusInput();
                }}
                className="px-3 py-1.5 rounded-full text-xs font-mono text-white/60 border border-white/10 hover:border-mint-400/50 hover:text-mint-300 hover:bg-mint-400/5 transition-colors"
              >
                {c}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="mt-6 flex items-center justify-center gap-5 text-white/30">
          <a
            href="https://github.com/mtauqeer248"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-mint-300 transition-colors interactive"
            aria-label="GitHub"
          >
            <Github className="w-5 h-5" />
          </a>
          <a
            href="https://www.linkedin.com/in/muhammad-tauqeer-react-developer/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-mint-300 transition-colors interactive"
            aria-label="LinkedIn"
          >
            <Linkedin className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Terminal;
